/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiField} from "./UiField";
import {UiMultiLineTextFieldConfig, UiMultiLineTextFieldSubCommandHandler, UiMultiLineTextFieldSubEventSource} from "../../generated/UiMultiLineTextFieldConfig";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {Constants, escapeHtml, hasVerticalScrollBar} from "../Common";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {UiTextInputHandlingField_SpecialKeyPressedSubEvent, UiTextInputHandlingField_TextInputSubEvent} from "../../generated/UiTextInputHandlingFieldConfig";
import {UiSpecialKey} from "../../generated/UiSpecialKey";
import {keyCodes} from "trivial-components";
import {executeWhenAttached} from "../util/ExecuteWhenAttached";
import {bind} from "../util/Bind";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiMultiLineTextField extends UiField<UiMultiLineTextFieldConfig, string> implements UiMultiLineTextFieldSubEventSource, UiMultiLineTextFieldSubCommandHandler {

	public readonly onTextInput: TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent>(this, 250);
	public readonly onSpecialKeyPressed: TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent>(this, 250);

	private $wrapper: JQuery;
	private $field: JQuery;
	private $clearButton: JQuery;
	private showClearButton: boolean;
	private minHeight: number;
	private maxHeight: number;


	protected initialize(config: UiMultiLineTextFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$wrapper = $(`<div class="UiMultiLineTextField teamapps-input-wrapper">
	<textarea></textarea>
	<div class="clear-button tr-remove-button"/>  
</div>`);
		this.$field = this.$wrapper.find("textarea");
		this.$clearButton = this.$wrapper.find('.clear-button');
		this.$clearButton.click(() => {
			this.$field.val("");
			this.commit();
			this.updateClearButton();
		});

		this.setEmptyText(config.emptyText);
		this.setMaxCharacters(config.maxCharacters);
		this.setShowClearButton(config.showClearButton);

		this.$field.on('focus', (e) => {
			if (this.getEditingMode() !== UiFieldEditingMode.READONLY) {
				this.$wrapper.addClass("focus");
			}
		});
		this.$field.on('blur', (e) => {
			if (this.getEditingMode() !== UiFieldEditingMode.READONLY) {
				this.commit();
				this.updateClearButton();
				this.$wrapper.removeClass("focus");
			}
		});
		this.$field.on("input", () => {
			this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(this.$field.val().toString()));
			this.updateClearButton();
		});
		this.$field.on("keydown", (e) => {
			if (e.keyCode === keyCodes.escape) {
				this.displayCommittedValue(); // back to committedValue
				this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(this.$field.val().toString()));
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ESCAPE));
			} else if (e.keyCode === keyCodes.enter) {
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ENTER));
			}
		});

		this.$wrapper.appendTo(targetElement);
		this.updateClearButton();

		this.setMinHeight(config.minHeight);
		this.setMaxHeight(config.maxHeight);

		this.$field.on('input', () => this.updateTextareaHeight());
		this.updateTextareaHeight();
	}

	isValidData(v: string): boolean {
		return v == null || typeof v === "string";
	}

	@executeWhenAttached(true)
	private updateTextareaHeight() {
		this.$field[0].style.height = '0px';
		this.$field[0].style.height = (Math.max(this.minHeight - 2, Math.min(this.$field[0].scrollHeight, this.maxHeight - 2))) + 'px';
	}

	setMaxCharacters(maxCharacters: number): void {
		this.$field.attr('maxlength', maxCharacters || "");
	}

	setShowClearButton(showClearButton: boolean): void {
		this.showClearButton = showClearButton;
		this.updateClearButton();
	}

	@executeWhenAttached()
	private updateClearButton() {
		this.$wrapper.toggleClass("clearable", !!(this.showClearButton && this.$field.val()));
		this.$clearButton.css("right", hasVerticalScrollBar(this.$field[0]) ? Constants.SCROLLBAR_WIDTH + "px" : 0);
	}

	setEmptyText(emptyText: string): void {
		this.$field.attr("placeholder", emptyText || "");
	}

	public getMainDomElement(): JQuery {
		return this.$wrapper;
	}

	public getFocusableElement(): JQuery {
		return this.$field;
	}

	protected displayCommittedValue(): void {
		let value = this.getCommittedValue();
		this.$field.val(value || "");
		this.updateClearButton();
		this.updateTextareaHeight();
	}

	public getTransientValue(): string {
		return this.$field.val().toString();
	}

	focus(): void {
		this.$field.focus();
	}

	append(s: string, scrollToBottom: boolean): void {
		let transientValue = this.getTransientValue();
		let transientValueString = (transientValue && transientValue) || '';
		this.setCommittedValue(transientValueString + s);
		if (scrollToBottom) {
			this.$field.scrollTop(10000000);
		}
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		UiField.defaultOnEditingModeChangedImpl(this);
	}

	destroy(): void {
		// nothing to do.
	}

	public static getReadOnlyHtml(config: UiMultiLineTextFieldConfig, value: string, context: TeamAppsUiContext, drawBorder: boolean): string {
		return `<div class="static-readonly-UiMultiLineTextField ${drawBorder ? "bordered" : ""}">${value == null ? "" : escapeHtml(value)}</div>`;
	}

	getDefaultValue(): string {
		return "";
	}

	public valuesChanged(v1: string, v2: string): boolean {
		return v1 !== v2;
	}

	public setMinHeight(minHeight: number) {
		this.minHeight = minHeight;
		this.$wrapper.css("min-height", minHeight ? minHeight + "px" : "");
		this.updateTextareaHeight();
	}

	public setMaxHeight(maxHeight: number) {
		this.maxHeight = maxHeight || Number.MAX_SAFE_INTEGER;
		this.$wrapper.css("max-height", maxHeight ? maxHeight + "px" : "");
		this.updateTextareaHeight();
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiMultiLineTextField", UiMultiLineTextField);